import clsx from 'clsx';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import FeatureCheck from 'components/FeatureCheck';
import PPLPricingBanner from 'components/PPLPricingBanner';
import PricingPlanCard from 'components/PricingPlanCard';
import Rating from 'components/Rating';
import Typography from 'components/Typography';
import { EventContext } from 'context/EventContext';
import useViewport from 'hooks/useViewport';

import classes from './PricingPlans.module.scss';

/* eslint-disable import/no-duplicates -- Swiper module CSS imports are side-effect imports. */
import 'swiper/css';
import 'swiper/css/pagination';
/* eslint-enable import/no-duplicates */

const PRICING_VALUES = {
  free: 'Free plan',
  'free-trial': 'Free trial',
  subscription: 'Subscription',
};

const SWIPER_BREAKPOINTS_CONFIG = {
  360: {
    slidesPerView: 1.2,
    centeredSlides: true,
    spaceBetween: 8,
  },
  600: {
    slidesPerView: 1.6,
    centeredSlides: false,
    spaceBetween: 16,
  },
  960: {
    slidesPerView: 2,
    centeredSlides: false,
    spaceBetween: 16,
  },
  1280: {
    slidesPerView: 3,
    centeredSlides: false,
    spaceBetween: 16,
  },
};

const PricingPlans = ({ listing, showPplPricingBanner }) => {
  const showPplCtas = showPplPricingBanner && !listing.is_premium;
  const showValueForMoney =
    !!listing.reviews.total && !listing.value_for_money_summary?.summary;
  const isChecked = (value) => listing.pricing?.categories?.includes(value);

  const { is } = useViewport();
  const isXs = is('xs');
  const pricingPlans = (listing.pricing?.plans ?? []).map((plan) => ({
    ...plan,
    currency: listing.pricing?.currency ?? '',
    currency_symbol: listing.pricing?.currency_symbol ?? '',
  }));

  return (
    <>
      {showValueForMoney && (
        <div className={classes.valueForMoneyRow}>
          <span className={classes.label}>Value for money rating: </span>
          <Rating.Link
            isCompact={true}
            size="small"
            hideTotals={true}
            average={listing.reviews.by_field.rating_value.toString()}
          />
        </div>
      )}

      <p
        className={clsx(classes.title, {
          [classes.noMarginTop]: !showValueForMoney,
        })}
      >
        Pricing plans
      </p>
      {isXs && (
        <Typography className={classes.detailsText}>
          Pricing details:
        </Typography>
      )}
      <div className={classes.plans}>
        {!isXs && (
          <Typography className={classes.detailsText}>
            Pricing details:
          </Typography>
        )}
        {Object.entries(PRICING_VALUES).map(([key, value]) => (
          <FeatureCheck checked={isChecked(key)} key={`feature_${key}`}>
            {value}
          </FeatureCheck>
        ))}
      </div>

      <div className={classes.carouselContainer}>
        <EventContext value={{ engagementComponent: 'pricing_plans' }}>
          <Swiper
            modules={[Pagination]}
            pagination={{ clickable: true, el: '#cr-pricing-pagination' }}
            breakpoints={SWIPER_BREAKPOINTS_CONFIG}
          >
            {pricingPlans.map((plan, index) => (
              <SwiperSlide key={`plan_${index}`}>
                <PricingPlanCard
                  key={plan.name}
                  listing={listing}
                  currentPlan={plan}
                  position={index}
                  showPplPricingBanner={showPplPricingBanner}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </EventContext>
        <div id="cr-pricing-pagination" className={classes.pagination} />
      </div>

      <PPLPricingBanner show={showPplCtas} />
    </>
  );
};

export default PricingPlans;
